import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import DbSourceEditor from './DbSourceEditor';
import SnowflakeSourceEditor from './SnowflakeSourceEditor';
import FileSourceEditor from './FileSourceEditor';
import MongoSourceEditor from './MongoSourceEditor';
import DynamoSourceEditor from './DynamoSourceEditor';
import { Alert, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { RiDatabase2Line } from 'react-icons/ri';
import { FaFolder, FaServer } from 'react-icons/fa';
import { SiAmazonaws, SiAmazondynamodb, SiMicrosoftazure, SiMicrosoftsharepoint, SiMongodb } from 'react-icons/si';
import Select from 'react-select';
import { listDrivers } from '../../Drivers/api';
import DriverLogo from '../../Drivers/DriverLogo';
import {
  customFilter,
  DataSourceContext,
  FormButtons,
  Input,
  Option,
  SingleValue,
} from 'DataSources/Utils';
import { FILE_SOURCE_TYPE, vfsSchemeToTypeOption, DEFAULT_FILE_EXCLUDE_FILTER }
  from 'DataSources/datasourcesApi';
import { getSourceLabel } from 'Utils';
import { normalizeEngineKey } from 'Utils/driverUtils';

// File-platform entries appended to the type dropdown alongside installed JDBC drivers.
// Each row sets connection.type='file' (the file-source sentinel) plus a vfsScheme that
// FileSourceEditor branches on for credential fields. customDatabaseType holds the
// dropdown identity ('file_local' etc) so an edited file row resolves back to its option.
const FILE_PLATFORM_OPTIONS = [
  { value: 'file_local', label: 'Local Files', engine: FILE_SOURCE_TYPE, vfsScheme: 'LOCAL', icon: <FaFolder color='#5C6473' /> },
  { value: 'file_s3', label: 'Amazon S3', engine: FILE_SOURCE_TYPE, vfsScheme: 'S3', icon: <SiAmazonaws color='#ff9900' /> },
  { value: 'file_azure', label: 'Azure Blob', engine: FILE_SOURCE_TYPE, vfsScheme: 'AZURE_BLOB', icon: <SiMicrosoftazure color='#0078d4' /> },
  { value: 'file_sftp', label: 'SFTP', engine: FILE_SOURCE_TYPE, vfsScheme: 'SFTP', icon: <FaServer color='#315DFF' /> },
  { value: 'file_sharepoint', label: 'SharePoint Online', engine: FILE_SOURCE_TYPE, vfsScheme: 'SHAREPOINT', icon: <SiMicrosoftsharepoint color='#038387' /> },
];

// NoSQL entries appended alongside the JDBC drivers and file platforms. Neither comes
// from the driver registry — the worker connects with native drivers (MongoDB driver /
// AWS SDK), so there is no JDBC driver row to list and the options are always available.
// Saving maps type 'mongodb'/'dynamodb' → datasourceType NOSQL in
// datasourcesApi.uiTypeToDatasourceType.
const NOSQL_OPTIONS = [
  { value: 'mongodb', label: 'MongoDB', engine: 'mongodb', icon: <SiMongodb color='#66b16a' /> },
  { value: 'dynamodb', label: 'DynamoDB', engine: 'dynamodb', icon: <SiAmazondynamodb color='#4053D6' /> },
];

const SourceForm = ({ saveDataSourceHandler, deleteDataSourceHandler }) => {
  const { connection, setConnection, setUnsavedChanges, fetched } = useContext(DataSourceContext);
  const { id } = useParams();
  const editorReady = !id || fetched;

  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setDriversLoading(true);
      setDriversError(null);
      try {
        setDrivers(await listDrivers());
      } catch (e) {
        setDriversError(e?.message || 'Could not load installed drivers');
      } finally {
        setDriversLoading(false);
      }
    };
    load();
  }, []);

  // One option per installed driver row. Dedup-by-typeName guards against transient list
  // churn. File-platform entries (LOCAL/S3/Azure/SFTP) are appended after the drivers so
  // both kinds live in one selector — the user picks "PostgreSQL" or "Amazon S3" the same way.
  const options = useMemo(() => {
    const seen = new Set();
    const driverOptions = drivers
      .filter((d) => d.available !== false)
      .filter((d) => {
        const key = d.typeName || d.id || d.driverId;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((d) => ({
        value: d.typeName || d.id || d.driverId,
        label: d.displayName || d.name || d.typeName,
        helperText: d.driverVersion || d.version || '',
        engine: d.engine || d.database,
        flavorId: d.flavorId,
        defaultPort: d.defaultPort,
        icon: <DriverLogo engine={d.engine || d.database} flavorId={d.flavorId} />,
      }));
    return [...driverOptions, ...NOSQL_OPTIONS, ...FILE_PLATFORM_OPTIONS];
  }, [drivers]);

  // Resolve the saved value to an option. Match in priority order:
  //   1. typeName (the new model — e.g. "ORACLE", "AZURE_SQLSERVER")
  //   2. engine (legacy connections that stored "mysql" / "oracledb" / etc.)
  // The engine fallback keeps existing data sources usable after the dropdown migration.
  // File-source fallback: if the row signals a file source but customDatabaseType
  // didn't survive the API round-trip (legacy rows / FILE_FOLDER), derive the option
  // value from vfsScheme so the dropdown still pre-fills on edit.
  const isFileSource = connection.type === FILE_SOURCE_TYPE
    || (typeof connection.datasourceType === 'string' && connection.datasourceType.startsWith('FILE_'));
  const fileFallbackValue = isFileSource ? vfsSchemeToTypeOption(connection.vfsScheme) : null;
  const selectedTypeName = connection.customDatabaseType || fileFallbackValue || connection.type || '';
  const lower = (selectedTypeName || '').toLowerCase();
  // Engine-alias-aware fallback (C2D-1112 / C2D-1113). Imported (Delphix) rows carry no
  // customDatabaseType and an engine string in the upstream vocabulary ("postgres" /
  // "rds_postgres"), and MySQL sources are served by the bundled MariaDB Connector/J (there is
  // no separate "mysql" driver row). Exact string matching above misses both, so the editor
  // declared the driver missing and refused to open the row. Fold both sides onto the driver
  // catalog's engine keys (postgres→postgresql, rds_* suffixes, etc.) and treat mysql⇄mariadb as
  // equivalent — the same equivalence driverUtils already uses for connectability.
  const foldMysql = (key) => (key === 'mysql' ? 'mariadb' : key);
  const wantedEngineKey = foldMysql(normalizeEngineKey(connection.engine || selectedTypeName || connection.type));
  const selectedOption =
    options.find((o) => o.value === selectedTypeName) ||
    options.find((o) => (o.value || '').toLowerCase() === lower) ||
    options.find((o) => (o.engine || '').toLowerCase() === lower) ||
    (wantedEngineKey
      ? options.find((o) => foldMysql(normalizeEngineKey(o.engine || o.value)) === wantedEngineKey)
      : null) ||
    null;
  const effectiveEngine = selectedOption?.engine || connection.type;

  // An imported/edited source whose type doesn't match any installed-driver option means
  // the JDBC driver it needs isn't loaded — so the editor body can't render and the form
  // would otherwise show an empty dropdown with no explanation. Detect that case (editing
  // an existing row, drivers loaded cleanly, but nothing resolved) and name the missing
  // driver so the user knows why the details won't load. File and NoSQL (MongoDB/DynamoDB)
  // sources never depend on an installed JDBC driver, so exclude them (they resolve from the
  // static NOSQL_OPTIONS entries anyway, but the guard keeps the warning honest even if that
  // changes).
  const isNoSqlSource = ['mongodb', 'dynamodb'].includes((connection.type || '').toLowerCase())
    || connection.datasourceType === 'NOSQL';
  const driverUnavailable =
    !!id && editorReady && !driversLoading && !driversError && !isFileSource && !isNoSqlSource && !selectedOption;
  const missingDriverName =
    connection.customDatabaseType || connection.type || selectedTypeName || 'unknown';

  const formProps = {
    type: effectiveEngine,
    connection,
    setConnection: (v) => {
      setUnsavedChanges(true);
      setConnection(v);
    },
  };

  const renderEditor = () => {
    if (effectiveEngine === 'snowflake') {
      return <SnowflakeSourceEditor {...formProps} />;
    }
    if (effectiveEngine === FILE_SOURCE_TYPE) {
      return <FileSourceEditor {...formProps} />;
    }
    if (effectiveEngine === 'mongodb') {
      return <MongoSourceEditor {...formProps} />;
    }
    if (effectiveEngine === 'dynamodb') {
      return <DynamoSourceEditor {...formProps} />;
    }
    return <DbSourceEditor {...formProps} />;
  };

  const adopted = !!connection.registeredServiceId && connection.managedBy === 'c2';
  // Three labels are possible:
  //   1. "Adopted from <integration>" — credentials supplied for an integration row.
  //   2. "Imported — needs credentials" — JSON-import row with no password yet.
  //   3. nothing — native row with credentials (default everyday case).
  // Case 2 carries the parallel meaning to the table's "Local (imported)" pill so the
  // editor signals what's left to do (enter a password, save) without a dedicated banner.
  const adoptedLabel = adopted
    ? `Adopted from ${connection.registeredServiceName || getSourceLabel(connection.registeredServiceId)}`
    : connection.needsCredentials === true && !connection.registeredServiceId
      ? 'Imported \u2014 needs credentials'
      : null;

  return (
    <Card className='shadow-sm widget-card'>
      <div className='widget-header p-3 text-left'>
        <RiDatabase2Line className='widget-header-icon mr-2' /> Edit data source
        {adoptedLabel && (
          <span className='ml-2' style={{ color: '#315DFF', fontSize: '0.85rem', fontWeight: 500 }}>
            ({adoptedLabel})
          </span>
        )}
      </div>
      <Card.Body>
        <Form
          autoComplete='new-password'
          onSubmit={async (e) => {
            e.preventDefault();
            await saveDataSourceHandler(connection);
          }}
        >
          {!editorReady ? (
            <Row className='text-center py-5'>
              <Col>
                <Spinner animation='border' role='status' className='mb-3' />
                <div className='text-muted'>Loading data source…</div>
              </Col>
            </Row>
          ) : (
            <>
              <Row className='text-left'>
                <Col>
                  <Form.Group>
                    <Form.Label htmlFor='select-source-dropdown' className='start-search-labels'>
                      Data source type
                    </Form.Label>
                    {driversLoading ? (
                      <div className='py-2'>
                        <Spinner animation='border' size='sm' className='mr-2' />
                        Loading installed drivers…
                      </div>
                    ) : driversError ? (
                      <Alert variant='danger' className='small'>
                        {driversError}
                      </Alert>
                    ) : options.length === 0 ? (
                      <Alert variant='warning' className='small'>
                        No drivers installed. An administrator can add drivers via Settings → JDBC
                        Drivers.
                      </Alert>
                    ) : (
                      <div data-testid="source-type-select">
                      <Select
                        components={{ SingleValue, Input, Option }}
                        options={options}
                        value={selectedOption}
                        onChange={(obj) => {
                          if (obj.engine === FILE_SOURCE_TYPE) {
                            // File platform pick — clear DB-only fields so a user who
                            // toggled away from a database selection mid-edit doesn't ship
                            // stale host/port/etc. on save.
                            setConnection({
                              ...connection,
                              type: FILE_SOURCE_TYPE,
                              customDatabaseType: obj.value,
                              vfsScheme: obj.vfsScheme,
                              // The user no longer picks a file type — a file source is Folder
                              // Search (discover everything under the root; the extension is the
                              // type). Existing typed rows keep their datasourceType on edit.
                              fileType: connection.fileType || 'FILE_GENERIC_FOLDER',
                              // Seed the exclusion filter on a NEW source only. It is a
                              // visible, editable starting point (see
                              // DEFAULT_FILE_EXCLUDE_FILTER for what it covers and why),
                              // never a hidden server-side default — so clearing the box
                              // really means "exclude nothing". `?? ` not `||` so a user
                              // who deliberately emptied it isn't re-seeded on every
                              // type re-pick.
                              fileNameExcludeFilter: connection.fileNameExcludeFilter
                                ?? DEFAULT_FILE_EXCLUDE_FILTER,
                              host: undefined,
                              port: undefined,
                              database: undefined,
                              schemaName: undefined,
                              jdbcUrl: undefined,
                              user: undefined,
                              password: undefined,
                              warehouse: undefined,
                              path: undefined,
                              privateKey: undefined,
                              protocol: undefined,
                              peering: undefined,
                              encrypt: undefined,
                              trustServerCertificate: undefined,
                              connectionProperties: undefined,
                              _connectionMode: undefined,
                            });
                            return;
                          }
                          // Preserve a user-supplied port over the driver's default. Driver
                          // defaults (e.g. 3306 for MARIADB) are truthy, so the previous
                          // `obj.defaultPort || connection.port` ordering silently overwrote
                          // a typed port whenever the user (re-)picked a driver.
                          setConnection({
                            ...connection,
                            type: obj.engine,
                            customDatabaseType: obj.value,
                            port: connection.port || obj.defaultPort,
                          });
                        }}
                        filterOption={customFilter}
                        isDisabled={!!id}
                        // Portal the menu out of the form's stacking context — the Prod
                        // switch painted through the open type menu (C2D-1127). It now sits
                        // directly under the name field, so the overlap matters more, not less.
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
                      />
                      </div>
                    )}
                    <Form.Text className='mt-3'>* Required fields</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              {driverUnavailable && (
                <Alert variant='danger' className='mt-3'>
                  Driver '{missingDriverName}' not available. This data source was imported
                  but its driver isn't installed, so its details can't be loaded or edited.
                  An administrator can add it via Settings → JDBC Drivers.
                </Alert>
              )}
              {selectedOption && renderEditor()}
              {/* One description box for every source type — the per-type editors each own
                  their own name field, but nothing about this one varies by engine. Stored in
                  connection_details.description and shown under the name on the Data Source
                  Risk Profile; blank simply renders nothing there. */}
              {selectedOption && (
                <Row className='text-left mb-3'>
                  <Col>
                    <Form.Label htmlFor='data-source-description'>Description</Form.Label>
                    <Form.Control
                      id='data-source-description'
                      data-testid='data-source-description'
                      as='textarea'
                      rows={2}
                      size='sm'
                      maxLength={500}
                      placeholder='What this data source holds, who owns it, anything the next person should know.'
                      value={connection.description || ''}
                      onChange={(e) => formProps.setConnection({ ...connection, description: e.target.value })}
                    />
                    <Form.Text className='text-muted'>
                      Optional. Shown under the name on the Data Source Risk Profile.
                    </Form.Text>
                  </Col>
                </Row>
              )}
            </>
          )}
          <FormButtons
            deleteDataSourceHandler={deleteDataSourceHandler}
            connection={connection}
          />
        </Form>
      </Card.Body>
    </Card>
  );
};

export default SourceForm;
